import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  MessageSquare, Image, LayoutGrid, Type, Plus, Trash2, 
  Link, Phone, MapPin, Calendar, Share2, ExternalLink,
  ChevronLeft, ChevronRight, Smartphone, Braces, User, Mail, Hash
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const SUGGESTION_TYPES = [
  { value: 'reply', label: 'Réponse suggérée', icon: MessageSquare },
  { value: 'url', label: 'Ouvrir URL', icon: ExternalLink },
  { value: 'dial', label: 'Appeler', icon: Phone },
  { value: 'location', label: 'Voir sur la carte', icon: MapPin },
  { value: 'share_location', label: 'Partager position', icon: Share2 },
  { value: 'calendar', label: 'Ajouter au calendrier', icon: Calendar }
];

const TRAFFIC_TYPES = [
  { value: 'PROMOTION', label: 'Promotion / Marketing' },
  { value: 'TRANSACTION', label: 'Transactionnel' },
  { value: 'AUTHENTICATION', label: 'Authentification (OTP)' },
  { value: 'SERVICEREQUEST', label: 'Service client' },
  { value: 'ACKNOWLEDGEMENT', label: 'Confirmation' }
];

const MEDIA_HEIGHTS = [
  { value: 'SHORT', label: 'Petit (112dp)' },
  { value: 'MEDIUM', label: 'Moyen (168dp)' },
  { value: 'TALL', label: 'Grand (264dp)' }
];

const CARD_ORIENTATIONS = [
  { 
    value: 'VERTICAL', 
    label: 'Verticale', 
    description: 'Image en haut (ratios 2:1, 16:9, 7:3)' 
  },
  { 
    value: 'HORIZONTAL', 
    label: 'Horizontale', 
    description: 'Image à gauche (ratio 3:4)' 
  }
];

// Variables disponibles pour personnalisation
const AVAILABLE_VARIABLES = [
  { key: 'prenom', label: 'Prénom', icon: User, example: 'Jean' },
  { key: 'nom', label: 'Nom', icon: User, example: 'Dupont' },
  { key: 'email', label: 'Email', icon: Mail, example: 'jean@example.com' },
  { key: 'telephone', label: 'Téléphone', icon: Phone, example: '+33612345678' },
  { key: 'entreprise', label: 'Entreprise', icon: Type, example: 'Acme Corp' },
  { key: 'reference', label: 'Référence', icon: Hash, example: 'CMD-12345' },
  { key: 'montant', label: 'Montant', icon: Type, example: '99.99€' },
  { key: 'date', label: 'Date', icon: Calendar, example: '21/01/2026' },
  { key: 'lien', label: 'Lien personnalisé', icon: Link, example: 'https://...' },
  { key: 'code', label: 'Code promo', icon: Hash, example: 'PROMO20' }
];

const IMAGE_ASPECT_RATIOS = {
  VERTICAL: [
    { value: '2:1', label: '2:1 (Large)' },
    { value: '16:9', label: '16:9 (Standard)' },
    { value: '7:3', label: '7:3 (Panoramique)' }
  ],
  HORIZONTAL: [
    { value: '3:4', label: '3:4 (Portrait)' }
  ]
};

export default function RCSMessageEditor({ 
  initialContent = null, 
  onSave, 
  onPreviewChange,
  mode = 'edit' // 'edit' or 'create'
}) {
  const [messageType, setMessageType] = useState('text');
  const [trafficType, setTrafficType] = useState('PROMOTION');
  
  // Text content
  const [text, setText] = useState('');
  
  // Rich card content
  const [cardTitle, setCardTitle] = useState('');
  const [cardDescription, setCardDescription] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaHeight, setMediaHeight] = useState('MEDIUM');
  const [cardOrientation, setCardOrientation] = useState('VERTICAL');
  const [imageAspectRatio, setImageAspectRatio] = useState('16:9');
  
  // Carousel
  const [carouselCards, setCarouselCards] = useState([
    { title: '', description: '', media_url: '', media_height: 'MEDIUM', orientation: 'VERTICAL', suggestions: [] },
    { title: '', description: '', media_url: '', media_height: 'MEDIUM', orientation: 'VERTICAL', suggestions: [] }
  ]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  
  // Suggestions
  const [suggestions, setSuggestions] = useState([]);
  
  // Track initialization state to prevent infinite loops
  const isInitializingRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);
  
  // Initialize from initial content - only once on mount or when key changes
  useEffect(() => {
    if (initialContent && !hasInitializedRef.current) {
      isInitializingRef.current = true;
      hasInitializedRef.current = true;
      
      if (initialContent.text) {
        setMessageType('text');
        setText(initialContent.text);
      }
      // Support both 'cards' and 'carousel_cards' property names
      if (initialContent.cards || initialContent.carousel_cards) {
        setMessageType('carousel');
        setCarouselCards(initialContent.cards || initialContent.carousel_cards);
      }
      if (initialContent.title || initialContent.media_url) {
        setMessageType('rich_card');
        setCardTitle(initialContent.title || '');
        setCardDescription(initialContent.description || '');
        setMediaUrl(initialContent.media_url || '');
        setMediaHeight(initialContent.media_height || 'MEDIUM');
      }
      if (initialContent.suggestions) {
        setSuggestions(initialContent.suggestions);
      }
      
      // Reset the initializing flag after a short delay to allow state to settle
      setTimeout(() => {
        isInitializingRef.current = false;
      }, 100);
    }
  }, []); // Empty dependency - only run once on mount

  // Build content object
  const buildContent = () => {
    let content = {};
    
    switch (messageType) {
      case 'text':
        content = { text, suggestions };
        break;
      case 'media':
        content = { media_url: mediaUrl, suggestions };
        break;
      case 'rich_card':
        content = {
          title: cardTitle,
          description: cardDescription,
          media_url: mediaUrl,
          media_height: mediaHeight,
          orientation: cardOrientation,
          aspect_ratio: imageAspectRatio,
          suggestions: suggestions.slice(0, 4) // RCS limit: 4 buttons max
        };
        break;
      case 'carousel':
        content = { cards: carouselCards.map(c => ({...c, suggestions: (c.suggestions || []).slice(0, 4)})) };
        break;
      default:
        content = { text };
    }
    
    return {
      message_type: messageType,
      traffic_type: trafficType,
      content
    };
  };

  // Notify parent of content changes - with proper dependencies
  const prevContentRef = React.useRef(null);
  
  useEffect(() => {
    // Skip during initialization to prevent infinite loops
    if (isInitializingRef.current) {
      return;
    }
    
    const content = buildContent();
    const contentStr = JSON.stringify(content);
    
    if (onPreviewChange && contentStr !== prevContentRef.current) {
      prevContentRef.current = contentStr;
      onPreviewChange(content);
    }
  }, [messageType, trafficType, text, cardTitle, cardDescription, mediaUrl, mediaHeight, cardOrientation, imageAspectRatio, carouselCards, suggestions, onPreviewChange]);

  const addSuggestion = useCallback((type) => {
    const timestamp = Date.now();
    const newSuggestion = {
      id: timestamp,
      type,
      text: '',
      postback_data: `action_${timestamp}`
    };
    
    if (type === 'url') {
      newSuggestion.url = '';
      newSuggestion.webview = false;
    } else if (type === 'dial') {
      newSuggestion.phone_number = '';
    } else if (type === 'location') {
      newSuggestion.latitude = 48.8566;
      newSuggestion.longitude = 2.3522;
      newSuggestion.location_label = '';
    } else if (type === 'calendar') {
      newSuggestion.calendar_title = '';
      newSuggestion.calendar_description = '';
      newSuggestion.calendar_start = '';
      newSuggestion.calendar_end = '';
    }
    
    setSuggestions(prev => [...prev, newSuggestion]);
  }, []);

  const updateSuggestion = (index, field, value) => {
    const updated = [...suggestions];
    updated[index][field] = value;
    setSuggestions(updated);
  };

  const removeSuggestion = (index) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };

  const addCarouselCard = () => {
    if (carouselCards.length < 10) {
      setCarouselCards([
        ...carouselCards,
        { title: '', description: '', media_url: '', media_height: 'MEDIUM', suggestions: [] }
      ]);
    }
  };

  const removeCarouselCard = (index) => {
    if (carouselCards.length > 2) {
      const updated = carouselCards.filter((_, i) => i !== index);
      setCarouselCards(updated);
      if (activeCardIndex >= updated.length) {
        setActiveCardIndex(updated.length - 1);
      }
    }
  };

  const updateCarouselCard = (index, field, value) => {
    const updated = [...carouselCards];
    updated[index][field] = value;
    setCarouselCards(updated);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(buildContent());
    }
  };

  const renderSuggestionEditor = (suggestion, index) => {
    const SuggestionIcon = SUGGESTION_TYPES.find(t => t.value === suggestion.type)?.icon || MessageSquare;
    
    return (
      <div key={suggestion.id} className="border rounded-lg p-3 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <SuggestionIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">
              {SUGGESTION_TYPES.find(t => t.value === suggestion.type)?.label}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => removeSuggestion(index)}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="space-y-2">
          <Input
            placeholder="Texte du bouton (max 25 car.)"
            value={suggestion.text}
            onChange={(e) => updateSuggestion(index, 'text', e.target.value.slice(0, 25))}
            maxLength={25}
          />
          
          {suggestion.type === 'url' && (
            <>
              <Input
                placeholder="https://example.com"
                value={suggestion.url || ''}
                onChange={(e) => updateSuggestion(index, 'url', e.target.value)}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={suggestion.webview || false}
                  onChange={(e) => updateSuggestion(index, 'webview', e.target.checked)}
                />
                Ouvrir en webview
              </label>
            </>
          )}
          
          {suggestion.type === 'dial' && (
            <Input
              placeholder="+33123456789"
              value={suggestion.phone_number || ''}
              onChange={(e) => updateSuggestion(index, 'phone_number', e.target.value)}
            />
          )}
          
          {suggestion.type === 'location' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Latitude"
                  value={suggestion.latitude || ''}
                  onChange={(e) => updateSuggestion(index, 'latitude', parseFloat(e.target.value))}
                />
                <Input
                  type="number"
                  placeholder="Longitude"
                  value={suggestion.longitude || ''}
                  onChange={(e) => updateSuggestion(index, 'longitude', parseFloat(e.target.value))}
                />
              </div>
              <Input
                placeholder="Nom du lieu"
                value={suggestion.location_label || ''}
                onChange={(e) => updateSuggestion(index, 'location_label', e.target.value)}
              />
            </>
          )}
          
          {suggestion.type === 'calendar' && (
            <>
              <Input
                placeholder="Titre de l'événement"
                value={suggestion.calendar_title || ''}
                onChange={(e) => updateSuggestion(index, 'calendar_title', e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Début</Label>
                  <Input
                    type="datetime-local"
                    value={suggestion.calendar_start || ''}
                    onChange={(e) => updateSuggestion(index, 'calendar_start', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Fin</Label>
                  <Input
                    type="datetime-local"
                    value={suggestion.calendar_end || ''}
                    onChange={(e) => updateSuggestion(index, 'calendar_end', e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="rcs-message-editor">
      {/* Message Type Selection */}
      <div className="flex gap-2">
        <Button
          variant={messageType === 'text' ? 'default' : 'outline'}
          onClick={() => setMessageType('text')}
          className={messageType === 'text' ? 'bg-indigo-600' : ''}
        >
          <Type className="w-4 h-4 mr-2" />
          Texte
        </Button>
        <Button
          variant={messageType === 'media' ? 'default' : 'outline'}
          onClick={() => setMessageType('media')}
          className={messageType === 'media' ? 'bg-indigo-600' : ''}
        >
          <Image className="w-4 h-4 mr-2" />
          Média
        </Button>
        <Button
          variant={messageType === 'rich_card' ? 'default' : 'outline'}
          onClick={() => setMessageType('rich_card')}
          className={messageType === 'rich_card' ? 'bg-indigo-600' : ''}
        >
          <Smartphone className="w-4 h-4 mr-2" />
          Rich Card
        </Button>
        <Button
          variant={messageType === 'carousel' ? 'default' : 'outline'}
          onClick={() => setMessageType('carousel')}
          className={messageType === 'carousel' ? 'bg-indigo-600' : ''}
        >
          <LayoutGrid className="w-4 h-4 mr-2" />
          Carrousel
        </Button>
      </div>

      {/* Traffic Type */}
      <div className="space-y-2">
        <Label>Type de trafic</Label>
        <Select value={trafficType} onValueChange={setTrafficType}>
          <SelectTrigger data-testid="traffic-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRAFFIC_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content Editor based on type */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Contenu du message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Text Message */}
          {messageType === 'text' && (
            <div className="space-y-2">
              <Label>Texte du message (max 3072 car.)</Label>
              <Textarea
                id="message-text-area"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 3072))}
                placeholder="Votre message... Utilisez {{variable}} pour personnaliser"
                rows={4}
                data-testid="message-text-input"
              />
              <div className="flex items-center justify-between">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      data-testid="insert-variable-btn"
                    >
                      <Braces className="w-4 h-4 mr-2" />
                      Insérer une variable
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-2" align="start">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 px-2 py-1">
                        Variables disponibles
                      </p>
                      {AVAILABLE_VARIABLES.map((variable) => {
                        const IconComponent = variable.icon;
                        return (
                          <button
                            key={variable.key}
                            type="button"
                            className="w-full flex items-center gap-3 px-2 py-2 text-sm rounded-md hover:bg-indigo-50 transition-colors text-left"
                            onClick={() => {
                              const textarea = document.getElementById('message-text-area');
                              const start = textarea?.selectionStart || text.length;
                              const end = textarea?.selectionEnd || text.length;
                              const newText = text.slice(0, start) + `{{${variable.key}}}` + text.slice(end);
                              setText(newText.slice(0, 3072));
                              // Focus back on textarea
                              setTimeout(() => {
                                textarea?.focus();
                                const newPos = start + variable.key.length + 4;
                                textarea?.setSelectionRange(newPos, newPos);
                              }, 0);
                            }}
                            data-testid={`variable-${variable.key}`}
                          >
                            <IconComponent className="w-4 h-4 text-indigo-500" />
                            <div className="flex-1">
                              <div className="font-medium text-gray-700">
                                {`{{${variable.key}}}`}
                              </div>
                              <div className="text-xs text-gray-400">
                                {variable.label} • ex: {variable.example}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-gray-400 px-2">
                        💡 Les variables seront remplacées par les données de vos contacts lors de l'envoi
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="text-xs text-gray-500">
                  {text.length}/3072
                </div>
              </div>
            </div>
          )}

          {/* Media Message */}
          {messageType === 'media' && (
            <div className="space-y-2">
              <Label>URL du média</Label>
              <Input
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                data-testid="media-url-input"
              />
              <p className="text-xs text-gray-500">
                Formats supportés: JPEG, PNG, GIF, MP4, WEBM, PDF
              </p>
            </div>
          )}

          {/* Rich Card */}
          {messageType === 'rich_card' && (
            <div className="space-y-4">
              {/* Card Orientation Selection */}
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <Label className="text-sm font-medium text-indigo-900 mb-3 block">
                  Orientation de la carte
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {CARD_ORIENTATIONS.map(orient => (
                    <button
                      key={orient.value}
                      type="button"
                      onClick={() => {
                        setCardOrientation(orient.value);
                        // Set default aspect ratio for orientation
                        setImageAspectRatio(orient.value === 'VERTICAL' ? '16:9' : '3:4');
                      }}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        cardOrientation === orient.value 
                          ? 'border-indigo-600 bg-indigo-100' 
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{orient.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{orient.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Titre</Label>
                <Input
                  id="card-title-input"
                  value={cardTitle}
                  onChange={(e) => setCardTitle(e.target.value)}
                  placeholder="Titre de la carte - utilisez {{variable}}"
                  data-testid="card-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description (sous-titre)</Label>
                <Textarea
                  id="card-description-area"
                  value={cardDescription}
                  onChange={(e) => setCardDescription(e.target.value)}
                  placeholder="Description de la carte - utilisez {{variable}} pour personnaliser"
                  rows={3}
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                    >
                      <Braces className="w-4 h-4 mr-2" />
                      Insérer une variable
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2" align="start">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 px-2 py-1">Variables</p>
                      {AVAILABLE_VARIABLES.slice(0, 6).map((variable) => {
                        const IconComponent = variable.icon;
                        return (
                          <button
                            key={variable.key}
                            type="button"
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-indigo-50"
                            onClick={() => {
                              setCardDescription(prev => prev + `{{${variable.key}}}`);
                            }}
                          >
                            <IconComponent className="w-3 h-3 text-indigo-500" />
                            <span className="text-gray-700">{`{{${variable.key}}}`}</span>
                          </button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL du média</Label>
                  <Input
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Ratio d&apos;aspect</Label>
                  <Select value={imageAspectRatio} onValueChange={setImageAspectRatio}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {IMAGE_ASPECT_RATIOS[cardOrientation].map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Hauteur du média</Label>
                <Select value={mediaHeight} onValueChange={setMediaHeight}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_HEIGHTS.map(h => (
                      <SelectItem key={h.value} value={h.value}>
                        {h.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Info about RCS limitations */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Limitation RCS :</strong> Maximum 4 boutons par carte
                </p>
              </div>
            </div>
          )}

          {/* Carousel */}
          {messageType === 'carousel' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveCardIndex(Math.max(0, activeCardIndex - 1))}
                    disabled={activeCardIndex === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium">
                    Carte {activeCardIndex + 1} / {carouselCards.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActiveCardIndex(Math.min(carouselCards.length - 1, activeCardIndex + 1))}
                    disabled={activeCardIndex === carouselCards.length - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addCarouselCard}
                    disabled={carouselCards.length >= 10}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeCarouselCard(activeCardIndex)}
                    disabled={carouselCards.length <= 2}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Titre de la carte</Label>
                    <Input
                      value={carouselCards[activeCardIndex]?.title || ''}
                      onChange={(e) => updateCarouselCard(activeCardIndex, 'title', e.target.value)}
                      placeholder="Titre"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={carouselCards[activeCardIndex]?.description || ''}
                      onChange={(e) => updateCarouselCard(activeCardIndex, 'description', e.target.value)}
                      placeholder="Description"
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>URL du média</Label>
                      <Input
                        value={carouselCards[activeCardIndex]?.media_url || ''}
                        onChange={(e) => updateCarouselCard(activeCardIndex, 'media_url', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Hauteur</Label>
                      <Select 
                        value={carouselCards[activeCardIndex]?.media_height || 'MEDIUM'}
                        onValueChange={(v) => updateCarouselCard(activeCardIndex, 'media_height', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MEDIA_HEIGHTS.map(h => (
                            <SelectItem key={h.value} value={h.value}>
                              {h.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Carousel indicators */}
              <div className="flex justify-center gap-1">
                {carouselCards.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveCardIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === activeCardIndex ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Suggestions Editor */}
      {messageType !== 'carousel' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Suggestions</span>
              <Badge variant="outline">{suggestions.length}/11</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add suggestion buttons */}
            <div className="flex flex-wrap gap-2">
              {SUGGESTION_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <Button
                    key={type.value}
                    variant="outline"
                    size="sm"
                    onClick={() => addSuggestion(type.value)}
                    disabled={suggestions.length >= 11}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {type.label}
                  </Button>
                );
              })}
            </div>
            
            {/* Suggestions list */}
            <div className="space-y-3">
              {suggestions.map((suggestion, index) => renderSuggestionEditor(suggestion, index))}
            </div>
            
            {suggestions.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                Ajoutez des suggestions pour permettre aux utilisateurs d&apos;interagir avec votre message
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      {onSave && (
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-message-btn"
          >
            Enregistrer le message
          </Button>
        </div>
      )}
    </div>
  );
}
